/**
 * Conversion between the plain text the AI endpoints speak and the HTML the
 * composer's RichTextEditor holds.
 *
 * Shared because getting it wrong is invisible until it reaches a customer:
 * dropping a plain draft straight into the editor collapses every blank line,
 * so a greeting, three paragraphs and a sign-off arrive as one run-on block.
 */

/** Flatten editor HTML to the plain text an AI endpoint expects. */
export function htmlToPlain(s: string): string {
  return s
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Turn a plain-text draft into editor HTML.
 *
 * A blank line starts a new `<p>`; a single newline becomes `<br>`. Content is
 * escaped first — a customer name containing `&` or a quoted `<tag>` in the
 * draft would otherwise corrupt the markup or inject an element.
 */
export function plainToHtml(s: string): string {
  const escape = (t: string): string =>
    t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

  const paragraphs = s
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0)

  if (paragraphs.length === 0) return ''

  return paragraphs.map((p) => `<p>${escape(p).replace(/\n/g, '<br>')}</p>`).join('')
}
